import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { OnAppInit } from '@frontend/vanilla/core';
import { Observable, Subscription, finalize, merge, tap, timer } from 'rxjs';

interface IBackgroundWorkerSchedule {
    delay: number; // a delay in milliseconds until start run method for the first time
    interval: number; // interval in milliseconds until the next run method execution.
}

export abstract class RepeatBackgroundWorkerService {
    abstract getSchedule(): IBackgroundWorkerSchedule;

    abstract run(): void;

    abstract init(): void;

    abstract destroy(): void;
}

export abstract class LongRunningBackgroundWorkerService {
    abstract init(): void;

    abstract run(): void;

    abstract destroy(): void;
}

export const REPEAT_BACKGROUND_WORKER = new InjectionToken<RepeatBackgroundWorkerService>('REPEAT_BACKGROUND_WORKER');
export const LONG_RUNNING_BACKGROUND_WORKER = new InjectionToken<LongRunningBackgroundWorkerService>('LONG_RUNNING_BACKGROUND_WORKER');

@Injectable({
    providedIn: 'root',
})
export class BackgroundWorkerManagerService implements OnAppInit, OnDestroy {
    private readonly repeatBackgroundServices: RepeatBackgroundWorkerService[];
    private readonly longRunningBackgroundServices: LongRunningBackgroundWorkerService[];
    private repeatingServicesSubscription: Nullable<Subscription> = null;

    constructor() {
        const repeatBackgroundServices = inject<RepeatBackgroundWorkerService[]>(REPEAT_BACKGROUND_WORKER, { optional: true });
        const longRunningBackgroundServices = inject<LongRunningBackgroundWorkerService[]>(LONG_RUNNING_BACKGROUND_WORKER, { optional: true });

        this.repeatBackgroundServices = repeatBackgroundServices ?? [];
        this.longRunningBackgroundServices = longRunningBackgroundServices ?? [];
        if (!(Array.isArray(this.repeatBackgroundServices) && Array.isArray(this.longRunningBackgroundServices))) {
            throw new Error('Expecting array of services got something else!');
        }
    }

    onAppInit(): void | Promise<any> {
        this.runRepeatingServices();
        this.runLongRunningServices();
    }

    ngOnDestroy(): void {
        this.repeatingServicesSubscription?.unsubscribe();
        this.stopLongRunningServices();
    }

    private runRepeatingServices(): void {
        const services: Observable<number>[] = [];
        for (const service of this.repeatBackgroundServices) {
            services.push(this.setupRepeatTimer(service));
        }
        this.repeatingServicesSubscription = merge(...services).subscribe();
    }

    private setupRepeatTimer(service: RepeatBackgroundWorkerService): Observable<number> {
        const executionTime = service.getSchedule();
        service.init();

        return timer(executionTime.delay, executionTime.interval).pipe(
            tap((val) => {
                if (val === 0) {
                    service.init();
                }
                service.run();
            }),
            finalize(() => service.destroy()),
        );
    }

    private runLongRunningServices(): void {
        for (const service of this.longRunningBackgroundServices) {
            service.init();
            service.run();
        }
    }

    private stopLongRunningServices(): void {
        for (const service of this.longRunningBackgroundServices) {
            service.destroy();
        }
    }
}
